import React, { useEffect, useState } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { InteractionAudit } from '../../interfaces/interactionAudit';
import apiClient from '../../axiosClient';
import FieldItem from '../baseComponents/FieldItem';
import CaseDetail from '../../interfaces/caseDetail';
import CurrentCaseDetails from '../baseComponents/currentCaseDetails';
import InteractionHistoryDetails from '../baseComponents/interactionHistoryDetails';
import DispositionsSelector from './auditDispositionsSelector';
import AdditionalCaseDetailsData from '../baseComponents/additionaCaseDetailsData';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import { decodedJWT, extractInteractionLevel, getOwnerType } from '../../utils/commonUtils';
import {CustomerDetailResponse} from '../../interfaces/customerDetail';
import Case from '../../interfaces/case';
import AdditionalContactReferences from '../baseComponents/additionalContactReferences';

interface InteractionAuditDetailsProps {
    row: InteractionAudit;
    onClose: () => void;
    onNext: () => void;
    onPrevious: () => void;
    hasNext: boolean;
    hasPrevious: boolean;
}

const hideScrollbar = {
    '&::-webkit-scrollbar': { display: 'none' },  // Hides scrollbar for Webkit browsers
    '-ms-overflow-style': 'none',  // Hides scrollbar for IE and Edge
    'scrollbar-width': 'none'  // Hides scrollbar for Firefox
};

const InteractionAuditDetails: React.FC<InteractionAuditDetailsProps> = ({ row, onClose, onNext, onPrevious, hasNext, hasPrevious }) => {
    const [caseDetailData, setCaseDetailData] = useState<CaseDetail[]>([]);
    const [customerDetail, setCustomerDetail] = useState<CustomerDetailResponse[]>([]);
    const [currentCase,setCurrentCase] = useState<Case>();
    // dispositions values
    const [selectedDispositions, setSelectedDispositions] = useState<Record<number, string>>({});

    const token: JWTTokenPayload|false = decodedJWT();
    const owner_type = getOwnerType(token);

	const fetchCustomerDetailData = async () => {
		try {
			const response = await apiClient.get('/api/v1/customer_detail', {
				params: {
					client_name: row.client_name,
                    customer_id: currentCase?.customer_id,
				}
			});	
			if (response.status === 200) {
                setCustomerDetail(response.data as CustomerDetailResponse[]);
			} else {
				alert("Error while loading customer detail data list: fetching customer detail data" + ", Error: " + response.data.message);
			}
		} catch (error: any) {
			if (error.response) {
				alert('Error response data:' + error.response.data.message);
			} else if (error.request) {
				alert('No response received:' + error.request);
			} else {
				alert('Error message:' + error.message);
			}
		}
	};
    const fetchAdditionalData = async () => {
        try {
            const response = await apiClient.get('/api/v1/case_detail?case_id=' + row.case_id);
            if (response.status === 200) {
                const additionalData = CaseDetail.initialiseCaseDetail(response.data);
                setCaseDetailData(additionalData);
            } else {
                alert("Error while loading additional data list: fetching CaseDetail or Interaction data, Error: " + response.data.message);
            }
        } catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    };
    const fetchCase = async () => {
        try {
            const response = await apiClient.get('/api/v1/case/' + row.case_id);
            if (response.status === 200) {
                setCurrentCase( response.data.case as Case );
            } else {
                alert("Error while loading additional data list: fetching Case Error: " + response.data.message);
            }
        }catch (error: any) {
            if (error.response) {
                alert('Error response data:' + error.response.data.message);
            } else if (error.request) {
                alert('No response received:' + error.request);
            } else {
                alert('Error message:' + error.message);
            }
        }
    }

    useEffect(() => {
        fetchCase();
        fetchAdditionalData();
    }, [row]);

    useEffect(() => {
        if (currentCase){
            fetchCustomerDetailData();
        }
    }, [currentCase]);

    const handleSelectDisposition = (value: string, level: number) => {
        setSelectedDispositions((prev) => {
            const updatedDispositions: Record<number, string> = {
                ...prev,
                [level]: value
            };
            if (level === 0) {
                updatedDispositions[1] = "";
            }
            return updatedDispositions;
        });
    }

    return (
        <Box sx={{ height: '90vh', overflowX: 'auto', overflowY: 'hidden' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5" gutterBottom>
                    Interaction Audit Details
                </Typography>
                <Typography variant="h6" color="textSecondary">
                    Status: {row.status}
                </Typography>
            </Box>

            <Divider />
            <Box
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" }}
                gap={2}
                sx={{ backgroundColor: 'white' }}
                height='85vh'
            >
                {/* 1st column shows casedetails and additional details and audit attributes */}
                <Box sx={{ padding: 2, overflowY: 'scroll', maxHeight: '75vh', ...hideScrollbar }}>
                    <CurrentCaseDetails case_id={row.case_id} caseDetailData={caseDetailData}/>
                    <Divider />
                    <AdditionalCaseDetailsData
                        caseDetailData={caseDetailData}
                        customerDetailData={customerDetail}
                    />
                    <AdditionalContactReferences
                        client_reference_id={row.client_reference_id}
                        customerDetailData={customerDetail}
                    />
                    <Divider />
                    <Box sx={{marginBottom: '4px'}}>
                        {(owner_type === 'Client' && row.agency_audit_value) && 
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Audit by {row.agency_name}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, padding: 0.5, background: "#FFF7E6" }}>
                                    <Box>
                                        <FieldItem
                                            fieldName={"L0 Disposition"}
                                            value={extractInteractionLevel(row.agency_audit_value, 0, 1)}
                                            />
                                        <FieldItem
                                            fieldName={"L1 Disposition"}
                                            value={extractInteractionLevel(row.agency_audit_value, 1, 2)}
                                            />
                                        <FieldItem
                                            fieldName={"Next Interaction Required?"}
                                            value={extractInteractionLevel(row.agency_audit_value, 2)}
                                            />
                                    </Box>
                                    <Typography sx={{ fontWeight: 'bold', color: '#6e6e6e' }}>Comment :</Typography>
                                    <Typography sx={{ fontWeight: 'bold', color: '#708090' }}>{row.agency_audit_comment}</Typography>
                                </Box>
                            </Box>
                        }
                        {(owner_type === 'Agency' && row.client_audit_value) && 
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Audit by {row.client_name}
                                </Typography>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, padding: 0.5, background: "#FFF7E6" }}>
                                    <Box>
                                        <FieldItem
                                            fieldName={"L0 Disposition"}
                                            value={extractInteractionLevel(row.client_audit_value, 0, 1)}
                                            />
                                        <FieldItem
                                            fieldName={"L1 Disposition"}
                                            value={extractInteractionLevel(row.client_audit_value, 1, 2)}
                                            />
                                        <FieldItem
                                            fieldName={"Next Interaction Required?"}
                                            value={extractInteractionLevel(row.client_audit_value, 2)}
                                            />
                                    </Box>
                                    <Typography sx={{ fontWeight: 'bold', color: '#6e6e6e' }}>Comment :</Typography>
                                    <Typography sx={{ fontWeight: 'bold', color: '#708090' }}>{row.client_audit_comment}</Typography>
                                </Box>
                            </Box>
                        }
                    </Box>
                    <Divider />
                    <Typography variant="h6" gutterBottom>
                        Interaction Audit Attributes
                    </Typography>
                    {Object.entries(row.audit_attributes || {}).map(([displayName, attributeData]) => {
                        // attributeData is now { def_id: number, value: any }
                        const value = (attributeData as { def_id: number; value: any })?.value;
                        const displayValue = typeof value === 'boolean' ? value.toString() : value;
                        return (
                            <FieldItem key={displayName} fieldName={displayName} value={displayValue} />
                        );
                    })}
                    <Divider />
                </Box>
                {/* second column shows dispogition selector */}
                <Box display='flex' flexDirection='column' justifyContent='space-between' sx={{ overflowY: 'scroll', maxHeight: '80vh', maxWidth: '510px', ...hideScrollbar }}  >
                    <DispositionsSelector
                        selectedDispositions={selectedDispositions}
                        setSelectedDispositions={setSelectedDispositions}
                        handleSelectDisposition={handleSelectDisposition}
                        row={row}
                        onNext={onNext}
                    />
                    <Box m={2} display="flex" justifyContent="center">
                        <Button onClick={() => {
                            setSelectedDispositions({});
                            onPrevious();
                        }} disabled={!hasPrevious} variant="contained" sx={{ mr: 1 }}>
                            Previous
                        </Button>
                        <Button onClick={() => {
                            setSelectedDispositions({});
                            onNext();
                        }} disabled={!hasNext} variant="contained" sx={{ mr: 1 }}>
                            Next
                        </Button>
                        <Button onClick={onClose} variant="contained" color="secondary">
                            Close
                        </Button>
                    </Box>
                </Box>
                {/* third column shows interaction history */}
                <Box sx={{ maxHeight: '85vh', ...hideScrollbar }}>
                    <InteractionHistoryDetails
                        row={row}
                        client_name={row.client_name}
                        client_reference_id={row.client_reference_id}
                        case_id={row.case_id}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default InteractionAuditDetails;
